import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 border border-transparent font-semibold text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-gray-50 focus-visible:ring-inset',
  {
    variants: {
      variant: {
        default:
          'bg-brand text-white hover:bg-brand-hover disabled:bg-brand-disabled disabled:text-text-brand-disabled dark:text-text-default',
        destructive:
          'bg-red-500 text-text-secondary hover:bg-red-500/90 disabled:bg-bg-secondary disabled:text-text-secondary',
        outline:
          'border border-gray-500 bg-transparent text-text-secondary hover:border-gray-400 hover:bg-bg-secondary hover:text-text-default',
        tertiary:
          'bg-transparent text-text-secondary hover:bg-bg-tertiary hover:text-text-default',
        link: 'text-text-default underline-offset-4 hover:underline',
        accent: 'bg-cyan-600/10 text-text-accent hover:text-text-accent',
      },
      size: {
        default: 'h-[36px] gap-1.5 px-3 text-sm',
        xs: 'h-[34px] gap-1.5 px-3 text-xs',
        md: 'h-[40px] gap-1.5 px-3 text-sm',
        lg: 'h-[48px] gap-2 px-2 text-sm',
        icon: 'h-9 w-9',
        auto: 'h-auto p-4',
      },
      rounded: {
        full: 'rounded-full',
        lg: 'rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'full',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  ref?: React.RefObject<HTMLButtonElement | null>;
}

const Button = ({
  className,
  variant,
  size,
  rounded,
  asChild = false,
  isLoading = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref as React.RefObject<HTMLButtonElement>}
      {...props}
    >
      {asChild ? (
        props.children
      ) : (
        <>
          {isLoading ? (
            <Loader2
              className={cn(
                'h-4 w-4 animate-spin',
                size !== 'icon' && 'mr-2',
              )}
            />
          ) : null}
          {isLoading && size === 'icon' ? null : props.children}
        </>
      )}
    </Comp>
  );
};

Button.displayName = 'Button';

export { Button, buttonVariants };
